import { useCallback, useEffect, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, Clock, Users } from "lucide-react";
import { ExportPdfButton, TodayButton, MonthButton, todayISO, periodLabel } from "../components/ExportPdfButton";
import { PAY_KEYS, eur, downloadCsv } from "../lib/payroll";
import { FileDown } from "lucide-react";
import { DateInput } from "../components/DateInput";

const h1 = (x) => `${(x || 0).toFixed(1)}h`;


export default function Hours() {
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(todayISO());
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get(`/agents/hours/summary?start=${start}&end=${end}`);
      setRows(r.data);
    } finally { setLoading(false); }
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const totalViolations = rows.reduce((s, r) => s + r.rest_violations + r.weekly_violations, 0);

  return (
    <div className="p-8 max-w-[1800px] mx-auto" data-testid="hours-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Heures par agent</h1>
          <p className="text-sm text-slate-600">Agents actifs · heures découpées à minuit (heure de Paris) · brut estimé avec majorations en cascade (Paramètres &gt; Paie) · repos 11h, max 48h/sem</p>
        </div>
        <div className="flex gap-2 items-end">
          <TodayButton testid="hours-today" active={start === todayISO() && end === todayISO()} onClick={() => { setStart(todayISO()); setEnd(todayISO()); }} />
          <MonthButton testid="hours-month" start={start} end={end} onClick={(a, b) => { setStart(a); setEnd(b); }} />
          <L label="Du"><DateInput data-testid="hours-start" className="input" value={start} onChange={(e)=>setStart(e.target.value)}/></L>
          <L label="Au"><DateInput data-testid="hours-end" className="input" value={end} onChange={(e)=>setEnd(e.target.value)}/></L>
          <ExportPdfButton testid="hours-export-pdf" title="Heures par agent" subtitle={periodLabel(start, end)} filename={`Heures_${start}_${end}`}
            columns={["Agent", "Total", ...PAY_KEYS.map(([, l]) => l), "Brut estimé", "Pic sem.", "Alertes"]}
            rows={() => rows.map((r) => [r.name, h1(r.total_hours), ...PAY_KEYS.map(([k]) => h1(r.by_shift_hours[k])), eur(r.gross_amount), h1(r.weekly_max_hours), r.rest_violations + r.weekly_violations || "OK"])} />
          <button type="button" data-testid="hours-export-csv" className="inline-flex items-center gap-1.5 border border-slate-300 bg-white hover:bg-slate-50 px-3 py-2 rounded-md text-sm font-semibold"
            onClick={() => downloadCsv(`Heures_${start}_${end}.csv`, ["Agent", "Total heures", ...PAY_KEYS.map(([, l]) => `Heures ${l}`), "Taux horaire brut", "Heures sans taux", "Brut estimé"],
              rows.map((r) => [r.name, r.total_hours, ...PAY_KEYS.map(([k]) => r.by_shift_hours[k] || 0), r.hourly_rate ?? "", r.hours_without_rate, r.gross_amount].map((x) => (typeof x === "number" ? String(x).replace(".", ",") : x))))}>
            <FileDown className="h-4 w-4" />CSV
          </button>
        </div>
      </div>

      {totalViolations > 0 && (
        <div className="mb-4 border border-rose-200 bg-rose-50 rounded-md p-4 flex items-start gap-3" data-testid="hours-alerts">
          <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0"/>
          <div className="text-sm text-rose-800">
            <b>{totalViolations} alerte(s) de conformité légale</b> détectées sur la période. Cliquez sur un agent pour voir le détail.
          </div>
        </div>
      )}

      <div className="border border-slate-200 bg-white rounded-md overflow-x-auto">
        <table className="w-full text-xs whitespace-nowrap [&_th]:whitespace-normal [&_th]:align-bottom [&_td]:px-1.5 [&_th]:px-1.5">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-widest font-bold text-slate-500">
              <th className="px-2.5 py-3">Agent</th>
              <th className="px-2.5 py-3 text-right">Total heures</th>
              {PAY_KEYS.map(([k, l]) => <th key={k} className="px-2 py-3 text-right">{l}</th>)}
              <th className="px-2.5 py-3 text-right">Brut estimé</th>
              <th className="px-2.5 py-3 text-right">Pic semaine</th>
              <th className="px-2.5 py-3 text-center">Conformité</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={13} className="text-center text-slate-500 py-10 text-sm">Chargement…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={13} className="text-center text-slate-500 py-10 text-sm">Aucun agent.</td></tr>}
            {rows.map((r) => {
              const violations = r.rest_violations + r.weekly_violations;
              const peakOver = r.weekly_max_hours > 48;
              return (
                <tr key={r.agent_id} className={`hover:bg-slate-50/60 cursor-pointer ${selected===r.agent_id?"bg-sky-50/50":""}`} onClick={()=>setSelected(selected===r.agent_id?null:r.agent_id)}>
                  <td className="px-2.5 py-3 font-semibold">{r.name}</td>
                  <td className="px-2.5 py-3 text-right font-mono font-bold">{r.total_hours.toFixed(1)}h</td>
                  {PAY_KEYS.map(([k]) => <td key={k} className={`px-2 py-3 text-right font-mono ${r.by_shift_hours[k] ? "text-slate-700" : "text-slate-300"}`}>{h1(r.by_shift_hours[k])}</td>)}
                  <td className="px-2.5 py-3 text-right font-mono font-semibold" data-testid={`hours-gross-${r.agent_id}`} title={r.hours_without_rate ? `${r.hours_without_rate} h sans taux de paie (Paramètres > Paie / qualification)` : ""}>{eur(r.gross_amount)}{r.hours_without_rate > 0 && <span className="text-amber-600"> *</span>}</td>
                  <td className={`px-4 py-3 text-right font-mono ${peakOver?"text-rose-700 font-bold":""}`}>{r.weekly_max_hours.toFixed(1)}h{peakOver?" ⚠":""}</td>
                  <td className="px-2.5 py-3 text-center">
                    {violations === 0
                      ? <span className="bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">OK</span>
                      : <span className="bg-rose-100 text-rose-800 border border-rose-200 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">{violations} alerte(s)</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && <DetailPanel agentId={selected} start={start} end={end} onClose={()=>setSelected(null)}/>}

      <style>{`.input{border:1px solid #cbd5e1;border-radius:6px;padding:.4rem .6rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #0ea5e933}`}</style>
    </div>
  );
}

function DetailPanel({ agentId, start, end, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { http.get(`/agents/${agentId}/hours?start=${start}&end=${end}`).then(r => setData(r.data)).catch((e) => toast.error(formatApiError(e))); }, [agentId, start, end]);
  if (!data) return null;
  return (
    <div className="mt-4 border border-slate-200 bg-white rounded-md p-5" data-testid="hours-detail">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-bold">{data.agent_name} · détail</h3>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-xs font-semibold uppercase tracking-widest">Fermer</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat label="Total" value={`${data.total_hours.toFixed(1)}h`} icon={Clock}/>
        <Stat label="Vacations" value={data.vacations_count} icon={Users}/>
        <Stat label="Brut estimé" value={eur(data.gross_amount)}/>
        <Stat label="Taux horaire (qualif.)" value={data.hourly_rate ? eur(data.hourly_rate) : "—"}/>
        <Stat label="Alertes repos <11h" value={data.rest_violations.length} accent={data.rest_violations.length>0?"rose":"slate"}/>
        <Stat label="Semaines >48h" value={data.weekly_violations.length} accent={data.weekly_violations.length>0?"rose":"slate"}/>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <h4 className="font-display font-bold mb-2 text-sm">Par semaine</h4>
          <table className="w-full text-xs border border-slate-200 rounded-md overflow-hidden">
            <thead className="bg-slate-50"><tr><th className="text-left px-3 py-2 font-bold">Semaine ISO</th><th className="text-right px-3 py-2 font-bold">Heures</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {Object.entries(data.weekly_hours).map(([k, h]) => (
                <tr key={k}><td className="px-3 py-2">{k}</td><td className={`text-right font-mono ${h>48?"text-rose-700 font-bold":""}`}>{h.toFixed(1)}h{h>48?" ⚠":""}</td></tr>
              ))}
              {Object.keys(data.weekly_hours).length === 0 && <tr><td colSpan={2} className="text-center text-slate-500 py-3">Aucune donnée.</td></tr>}
            </tbody>
          </table>
        </div>
        <div>
          <h4 className="font-display font-bold mb-2 text-sm">Repos &lt; 11h détectés</h4>
          {data.rest_violations.length === 0 ? (
            <div className="text-xs text-slate-500 border border-slate-200 rounded-md p-3 text-center">Aucune violation.</div>
          ) : (
            <div className="border border-rose-200 bg-rose-50/50 rounded-md divide-y divide-rose-100">
              {data.rest_violations.map((v, i) => (
                <div key={i} className="p-3 text-xs">
                  Fin {v.previous_date} {v.previous_end} → début {v.next_date} {v.next_start} · <b className="text-rose-700">{v.rest_hours}h de repos</b>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent="slate", icon: Icon }) {
  const acc = { slate:"bg-slate-100 text-slate-700", rose:"bg-rose-100 text-rose-800" }[accent];
  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-400"/>}
      </div>
      <div className={`mt-1 inline-block rounded px-2 py-0.5 font-display text-xl font-extrabold ${acc}`}>{value}</div>
    </div>
  );
}

function L({ label, children }) { return <label className="block"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</label>; }
